import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { AttachmentPostInfo, OperationState } from '../../../store/appState';
import styled from 'styled-components/macro';
import * as Actions from '../../../store/actions';
import { App } from '../../../app';
import Button from '../../shared/Button';
import ContextualNotificationDisplay, { NotificationKey } from '../../shared/ContextualNotificationDisplay';

export interface ProfileAttachmentListProps {

}

export default function ProfileAttachmentList(): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);

    const list = state.profileAttachments ?? [];

    // Handle delete attachment errors and success
    useEffect(() => {
        if (state.deleteAttachmentState === OperationState.Failed && state.deleteAttachmentErrorMessage) {
            Actions.setContextualNotification(NotificationKey.ProfileAttachments, {
                type: 'error',
                title: 'Failed to delete attachment',
                message: state.deleteAttachmentErrorMessage,
                canDismiss: true
            });
        } else if (state.deleteAttachmentState === OperationState.Success) {
            Actions.setContextualNotification(NotificationKey.ProfileAttachments, {
                type: 'information',
                title: 'Attachment deleted successfully',
                canDismiss: true
            });
        } else if (state.deleteAttachmentState === OperationState.NotStarted) {
            // Clear notifications when state is reset
            Actions.clearContextualNotification(NotificationKey.ProfileAttachments);
        }
    }, [state.deleteAttachmentState, state.deleteAttachmentErrorMessage]);

    const onAttachmentDownloadClick = (evt: React.MouseEvent, item: AttachmentPostInfo): void => {
        evt.preventDefault();
        App.telemetry.postBusinessEvent("downloadattachment-clicked", { filename: item.fileName });
        Actions.downloadAttachment(item);
    }

    const onAttachmentDeleteClick = (evt: React.MouseEvent, item: AttachmentPostInfo): void => {
        evt.preventDefault();
        if (window.confirm(`Are you sure you want to delete "${item.fileName}"? This action cannot be undone.`)) {
            // Clear any existing notifications before starting the new delete operation
            Actions.clearContextualNotification(NotificationKey.ProfileAttachments);
            
            App.telemetry.postBusinessEvent("deleteattachment-clicked", { filename: item.fileName });
            Actions.deleteAttachment(item);
        }
    }

    return list && list.length !== 0 &&
        <Wrapper>
            <ContextualNotificationDisplay 
                notifications={state.contextualNotifications[NotificationKey.ProfileAttachments]} 
                notificationKey={NotificationKey.ProfileAttachments}
            />
            {list.map((item, i) => (
                <Attachment key={i}>
                    <AttachmentHeader>
                        <Button buttonType="link" size="compact" className="filename" onClick={(evt) => onAttachmentDownloadClick(evt, item)}>{App.StripAllHtml(item.fileName)}</Button>
                        <Button 
                            buttonType="link" 
                            size="compact" 
                            className="delete-btn"
                            onClick={(evt) => onAttachmentDeleteClick(evt, item)}
                            title="Delete attachment"
                        >
                            Delete
                        </Button>
                    </AttachmentHeader>
                    {item.rootPostTypeName && <div className="info">
                        Attached to {item.postTypeName && item.postTypeName !== item.rootPostTypeName ? `${item.postTypeName} under ` : ''}the {item.rootPostTypeName}&nbsp;
                        <a className="problem" href={App.getTicketPath(item.rootPostId, item.rootPostTitle, true)} target="_blank" rel="noopener noreferrer">{App.StripAllHtml(item.rootPostTitle)}</a>
                    </div>}
                    <div className="info">
                        {`Added ${App.ParseDateTimeToLocal(item.createdDate)}`}
                    </div>
                </Attachment>
            ))}
        </Wrapper>
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    >* + * {
        margin-top: 1.5rem;
    }
`;

const Attachment = styled.article`
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    margin-left: 1.5rem;
    margin-right: 1.5rem;

    .info {
        color: var(--color-on-background-lighter);
    }

    .filename {
        cursor: pointer;
        font-weight: normal;
        font-size: var(--font-size-default);
        text-align: left;
        flex: 1;
    }
`;

const AttachmentHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .delete-btn {
        color: var(--color-danger);
        font-size: var(--font-size-small);
        padding: 0.25rem 0.5rem;
        margin-left: 1rem;
        
        &:hover {
            color: var(--color-danger-hover);
            text-decoration: underline;
        }
    }
`;
